import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Zap, Clock, FileText, Camera, Building2,
  CheckCircle, ArrowRight, Star, Users, TrendingUp,
  Lock, Cloud, Bell, BarChart3, Smartphone, Globe,
  ChevronDown, Mail, Phone, MapPin, Award, Target,
  Cpu, Database, RefreshCw, FileCheck, Euro, Calendar
} from 'lucide-react';
import TrialRegistrationModal from '../components/TrialRegistrationModal';

const LandingPage = () => {
  const navigate = useNavigate();
  const [planAnual, setPlanAnual] = useState(true);
  const [faqOpen, setFaqOpen] = useState(null);
  const [showTrialModal, setShowTrialModal] = useState(false);

  const features = [
    {
      icon: Zap,
      title: 'Identificación Automática',
      description: 'El 70% de las multas se identifican automáticamente gracias a las asignaciones de conductores.',
      color: 'amber'
    },
    {
      icon: Camera,
      title: 'OCR Inteligente',
      description: 'Sube una foto o PDF de cualquier multa física y extraemos los datos automáticamente con IA.',
      color: 'violet'
    },
    {
      icon: Shield,
      title: 'Conexión DGT Directa',
      description: 'Consulta y sincroniza multas directamente desde la sede electrónica de la DGT.',
      color: 'primary'
    },
    {
      icon: Bell,
      title: 'Alertas Inteligentes',
      description: 'Sistema de alertas por niveles de urgencia. Nunca más te pasarás del plazo de 20 días.',
      color: 'rose'
    },
    {
      icon: BarChart3,
      title: 'Reportes y Analytics',
      description: 'Dashboard en tiempo real, exportación a Excel y reportes diarios automáticos.',
      color: 'emerald'
    },
    {
      icon: Building2,
      title: 'Multi-Empresa',
      description: 'Gestiona múltiples empresas desde una única plataforma centralizada.',
      color: 'sky'
    }
  ];

  const plans = [
    {
      name: 'Starter',
      description: 'Ideal para pequeñas flotas',
      monthlyPrice: 149,
      annualPrice: 1490,
      multas: 50,
      extraPrice: 3.50,
      features: [
        'Hasta 50 multas/mes',
        'Identificación automática',
        'OCR básico (fotos)',
        'Alertas por email',
        'Soporte por email',
        '1 usuario'
      ],
      highlighted: false
    },
    {
      name: 'Business',
      description: 'Para flotas en crecimiento',
      monthlyPrice: 399,
      annualPrice: 3990,
      multas: 200,
      extraPrice: 3.00,
      features: [
        'Hasta 200 multas/mes',
        'Todo de Starter +',
        'OCR avanzado (PDF)',
        'Conexión DGT directa',
        'Reportes Excel',
        'Hasta 5 usuarios',
        'Soporte prioritario'
      ],
      highlighted: true,
      badge: 'Más Popular'
    },
    {
      name: 'Enterprise',
      description: 'Para grandes operaciones',
      monthlyPrice: 799,
      annualPrice: 7990,
      multas: 500,
      extraPrice: 2.50,
      features: [
        'Hasta 500 multas/mes',
        'Todo de Business +',
        'API de integración',
        'Múltiples empresas',
        'Usuarios ilimitados',
        'SLA 99.9%',
        'Soporte 24/7',
        'Account manager'
      ],
      highlighted: false
    }
  ];

  const testimonials = [
    {
      quote: "Redujimos el tiempo de gestión de multas en un 80%. El ROI fue inmediato.",
      author: "María García",
      role: "Directora de Operaciones",
      company: "Transportes Rápidos S.L.",
      image: "https://randomuser.me/api/portraits/women/44.jpg"
    },
    {
      quote: "La conexión directa con DGT nos da tranquilidad. Ya no dependemos de intermediarios.",
      author: "Carlos Rodríguez",
      role: "CEO",
      company: "LogiFleet España",
      image: "https://randomuser.me/api/portraits/men/32.jpg"
    },
    {
      quote: "El OCR es impresionante. Subimos la foto y en segundos tenemos todos los datos.",
      author: "Ana Martínez",
      role: "Responsable de Flota",
      company: "Distribuciones del Norte",
      image: "https://randomuser.me/api/portraits/women/68.jpg"
    }
  ];

  const stats = [
    { value: '50K+', label: 'Multas Gestionadas', icon: FileText },
    { value: '99.9%', label: 'Uptime Garantizado', icon: Shield },
    { value: '70%', label: 'Identificación Automática', icon: Zap },
    { value: '<2min', label: 'Tiempo Medio Proceso', icon: Clock }
  ];

  const faqs = [
    {
      question: '¿Cómo funciona la identificación automática?',
      answer: 'Cuando mantienes actualizadas las asignaciones de conductores a vehículos, el sistema identifica automáticamente quién conducía en el momento de la infracción. Esto reduce el coste de 6€ a 4€ por multa.'
    },
    {
      question: '¿Qué pasa si no identifico en 20 días?',
      answer: 'Por ley, la empresa tiene 20 días naturales para identificar al conductor. Si no se hace, la empresa asume la responsabilidad de la multa y los puntos. Nuestro sistema de alertas te avisa con antelación.'
    },
    {
      question: '¿Necesito instalar algo?',
      answer: 'No. Es 100% cloud. Solo necesitas un navegador web moderno. Funciona en ordenador, tablet y móvil.'
    },
    {
      question: '¿Cómo funciona el OCR?',
      answer: 'Usa Amazon Textract, la misma tecnología que Amazon. Sube una foto o escaneo de la multa y extraemos automáticamente: matrícula, expediente, fecha, importe y organismo.'
    },
    {
      question: '¿Puedo conectar con mi ERP?',
      answer: 'Sí. Ofrecemos API REST completa y podemos desarrollar conectores personalizados para SAP, Navision, A3 y otros sistemas.'
    },
    {
      question: '¿Mis datos están seguros?',
      answer: 'Absolutamente. Infraestructura AWS con cifrado en reposo y en tránsito, backups diarios, cumplimiento RGPD y certificación ISO 27001 en proceso.'
    }
  ];

  const integrations = [
    { name: 'DGT', description: 'Conexión directa' },
    { name: 'GoodOk', description: 'Notificaciones' },
    { name: 'Amazon Textract', description: 'OCR' },
    { name: 'AWS', description: 'Infraestructura' },
    { name: 'API REST', description: 'Integración' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header/Navbar */}
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-glow">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900 font-display">Grupo Ibérica</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition">Funcionalidades</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition">Precios</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition">Clientes</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 transition">FAQ</a>
            </nav>
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/login')}
                className="text-slate-600 hover:text-slate-900 transition font-medium"
              >
                Iniciar Sesión
              </button>
              <button
                onClick={() => setShowTrialModal(true)}
                className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-5 py-2.5 rounded-xl font-medium hover:shadow-glow hover:-translate-y-0.5 transition-all"
              >
                Prueba Gratis
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-primary-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-primary-100 text-primary-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Zap className="w-4 h-4" />
                Automatiza el 70% de tus gestiones
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6 font-display">
                Gestión de Multas
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-primary-600"> Inteligente</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 leading-relaxed">
                Identifica conductores automáticamente, conecta directo con la DGT y procesa multas físicas con OCR.
                Todo en una plataforma segura y fácil de usar.
              </p>
              <div className="flex flex-wrap gap-4 mb-8">
                <button
                  onClick={() => setShowTrialModal(true)}
                  className="btn-primary text-lg flex items-center gap-2"
                >
                  Empezar Gratis <ArrowRight className="w-5 h-5" />
                </button>
                <button className="btn-secondary text-lg">
                  Ver Demo
                </button>
              </div>
              <div className="flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  14 días gratis
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Sin tarjeta de crédito
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Soporte incluido
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-soft-lg p-6 border border-slate-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-400"></div>
                </div>
                <img
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=500&fit=crop"
                  alt="Dashboard"
                  className="rounded-lg w-full"
                />
              </div>
              {/* Floating Cards */}
              <div className="absolute -left-8 top-1/4 bg-white rounded-xl shadow-soft-lg p-4 border border-slate-200 animate-bounce-slow">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Identificado</p>
                    <p className="text-sm text-slate-500">Automáticamente</p>
                  </div>
                </div>
              </div>
              <div className="absolute -right-4 bottom-1/4 bg-white rounded-xl shadow-soft-lg p-4 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Camera className="w-6 h-6 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">OCR Activo</p>
                    <p className="text-sm text-slate-500">95% precisión</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-primary-100 rounded-xl mb-3">
                  <stat.icon className="w-6 h-6 text-primary-600" />
                </div>
                <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                <p className="text-slate-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 font-display">
              Todo lo que necesitas para gestionar multas
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Una plataforma completa que automatiza el proceso de identificación de conductores y comunicación con organismos.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="stat-card border border-slate-200"
              >
                <div className={`w-14 h-14 bg-${feature.color}-100 rounded-2xl flex items-center justify-center mb-6`}>
                  <feature.icon className={`w-7 h-7 text-${feature.color}-600`} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{feature.title}</h3>
                <p className="text-slate-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 bg-gradient-to-br from-slate-50 via-primary-50/50 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 font-display">
              ¿Cómo funciona?
            </h2>
            <p className="text-xl text-slate-600">
              Tres simples pasos para automatizar tu gestión de multas
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Recibe la Multa',
                description: 'Automáticamente vía GoodOk, consultando DGT o subiendo foto/PDF.',
                icon: FileText
              },
              {
                step: '02',
                title: 'Identificación',
                description: 'El sistema identifica al conductor automáticamente o te ayuda a hacerlo.',
                icon: Users
              },
              {
                step: '03',
                title: 'Comunicación',
                description: 'Enviamos los datos al organismo y te confirmamos la gestión.',
                icon: CheckCircle
              }
            ].map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-white rounded-2xl p-8 shadow-soft border border-slate-200 relative z-10 overflow-hidden">
                  <span className="absolute top-4 right-4 text-7xl font-bold text-primary-50 select-none">{item.step}</span>
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center mb-4 shadow-glow">
                      <item.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-slate-600">{item.description}</p>
                  </div>
                </div>
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-0.5 bg-primary-200 z-0"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 font-display">
              Planes adaptados a tu negocio
            </h2>
            <p className="text-xl text-slate-600 mb-8">
              Sin sorpresas. Precio fijo mensual + multas extra si las necesitas.
            </p>
            <div className="inline-flex items-center bg-slate-100 rounded-xl p-1">
              <button
                onClick={() => setPlanAnual(false)}
                className={`px-6 py-2.5 rounded-lg font-medium transition ${!planAnual ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
              >
                Mensual
              </button>
              <button
                onClick={() => setPlanAnual(true)}
                className={`px-6 py-2.5 rounded-lg font-medium transition flex items-center gap-2 ${planAnual ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
              >
                Anual <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">-17%</span>
              </button>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan, index) => (
              <div
                key={index}
                className={`relative bg-white rounded-2xl border-2 p-8 ${
                  plan.highlighted
                    ? 'border-primary-500 shadow-glow scale-105'
                    : 'border-slate-200 hover:border-slate-300'
                } transition-all`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-4 py-1 rounded-full text-sm font-medium shadow-glow">
                      {plan.badge}
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <p className="text-slate-500">{plan.description}</p>
                </div>
                <div className="text-center mb-6">
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-5xl font-bold text-slate-900">
                      {planAnual ? Math.round(plan.annualPrice / 12) : plan.monthlyPrice}€
                    </span>
                    <span className="text-slate-500">/mes</span>
                  </div>
                  {planAnual && (
                    <p className="text-sm text-slate-500 mt-1">
                      Facturado anualmente ({plan.annualPrice}€)
                    </p>
                  )}
                  <p className="text-sm text-primary-600 mt-2 font-medium">
                    Hasta {plan.multas} multas incluidas
                  </p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => setShowTrialModal(true)}
                  className={`w-full py-3 rounded-xl font-semibold transition ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:shadow-glow'
                      : 'bg-slate-100 text-slate-900 hover:bg-slate-200'
                  }`}
                >
                  Empezar Ahora
                </button>
                <p className="text-center text-sm text-slate-500 mt-3">
                  +{plan.extraPrice}€ por multa adicional
                </p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <p className="text-slate-600 mb-4">¿Necesitas un plan personalizado para más de 500 multas/mes?</p>
            <a href="mailto:comercial@grupoiberica.com" className="text-primary-600 font-semibold hover:underline">
              Contacta con nosotros →
            </a>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-primary-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4 font-display">
              Lo que dicen nuestros clientes
            </h2>
            <p className="text-xl text-slate-300">
              Empresas que ya confían en nosotros
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/10 backdrop-blur rounded-2xl p-8 border border-white/20">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-white text-lg mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.author}
                    className="w-12 h-12 rounded-full"
                  />
                  <div>
                    <p className="font-semibold text-white">{testimonial.author}</p>
                    <p className="text-slate-400 text-sm">{testimonial.role}</p>
                    <p className="text-slate-500 text-sm">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4 font-display">
              Preguntas Frecuentes
            </h2>
            <p className="text-xl text-slate-600">
              Todo lo que necesitas saber antes de empezar
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="border border-slate-200 rounded-xl overflow-hidden"
              >
                <button
                  onClick={() => setFaqOpen(faqOpen === index ? null : index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition"
                >
                  <span className="font-semibold text-slate-900">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${faqOpen === index ? 'rotate-180' : ''}`} />
                </button>
                {faqOpen === index && (
                  <div className="px-6 py-4 bg-white">
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-500 to-primary-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4 font-display">
            ¿Listo para automatizar tu gestión de multas?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Empieza gratis hoy. Sin tarjeta de crédito. Sin compromiso.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setShowTrialModal(true)}
              className="bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold text-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
            >
              Empezar Prueba Gratuita
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white/10 transition-all">
              Solicitar Demo
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-white font-display">Grupo Ibérica</span>
              </div>
              <p className="text-sm leading-relaxed">
                La plataforma líder en gestión automatizada de multas de tráfico para flotas empresariales.
              </p>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Producto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition">Funcionalidades</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Precios</a></li>
                <li><a href="#" className="hover:text-white transition">Integraciones</a></li>
                <li><a href="#" className="hover:text-white transition">API</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Empresa</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition">Sobre Nosotros</a></li>
                <li><a href="#" className="hover:text-white transition">Blog</a></li>
                <li><a href="#" className="hover:text-white transition">Empleo</a></li>
                <li><a href="#" className="hover:text-white transition">Contacto</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-semibold mb-4">Contacto</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  comercial@grupoiberica.com
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  +34 900 123 456
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Madrid, España
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">© 2026 Sistema de Multas - Grupo Ibérica. Todos los derechos reservados.</p>
            <div className="flex gap-6 text-sm">
              <a href="#" className="hover:text-white transition">Privacidad</a>
              <a href="#" className="hover:text-white transition">Términos</a>
              <a href="#" className="hover:text-white transition">Cookies</a>
            </div>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 3s ease-in-out infinite;
        }
      `}</style>

      {/* Modal de registro trial */}
      <TrialRegistrationModal
        isOpen={showTrialModal}
        onClose={() => setShowTrialModal(false)}
      />
    </div>
  );
};

export default LandingPage;
